/**
 * Quy tắc License RPA phía màn hình (`license-utils.ts`, `LicenseFormDialog.tsx`, `LicenseRenewDialog.tsx` của native):
 * trạng thái hiển thị, ghép User OpenCore với License, lọc, KPI, kiểm biểu mẫu và dựng thân gửi máy chủ. Máy chủ vẫn là
 * chốt cuối (`modules/rpa/backend/src/license/license.ts`); kiểm ở đây để báo lỗi cạnh chỗ sai trước khi gửi.
 */
import type {
  LicenseRpa,
  LoaiLicense,
  ProjectOpenRpa,
  ThanCapLicense,
  ThanSuaLicense,
  ThietBiLicense,
  TrangThaiLicense,
  TrangThaiUserLicense,
  UserOpenCore,
} from '../types.ts';

const MS_NGAY = 86_400_000;

/** Ngưỡng "License sắp hết hạn" (cùng ngưỡng với màn Project). */
export const NGAY_SAP_HET_HAN = 60;
/** Một User một máy (mô hình mới của native); License cũ nhiều máy giữ nguyên khi sửa vì màn không gửi `maxRobots`. */
export const SO_MAY_MAC_DINH = 1;

export const TRANG_THAI_USER_LICENSE: readonly TrangThaiUserLicense[] = ['active', 'pending', 'expired', 'blocked', 'revoked'];
export const TRANG_THAI_KHI_CAP: readonly TrangThaiUserLicense[] = ['active', 'pending'];
export const TRANG_THAI_KHI_SUA: readonly TrangThaiUserLicense[] = ['active', 'pending', 'blocked', 'expired', 'revoked'];
export const LOC_THIET_BI = ['bound', 'unbound'] as const;

/** Hết hạn lúc 23:59:59.999 UTC của ngày `YYYY-MM-DD` (`hanDung` của backend); không có ngày là `NaN`. */
export function hanMs(expiredAt: string | null | undefined): number {
  const s = typeof expiredAt === 'string' ? expiredAt : '';
  return Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T23:59:59.999Z` : s);
}

/**
 * Trạng thái để hiện: database không tự chuyển `active → expired`, nên License quá hạn vẫn có thể ghi `active` —
 * tính lại ở đây như native.
 */
export function trangThaiHieuLuc(l: Pick<LicenseRpa, 'status' | 'is_lifetime' | 'expired_at'>, now = Date.now()): TrangThaiLicense {
  if (l.status === 'active' && !l.is_lifetime && !(hanMs(l.expired_at) > now)) return 'expired';
  return l.status;
}

const THEO_USER: Record<TrangThaiLicense, TrangThaiUserLicense> = {
  unused: 'pending',
  active: 'active',
  locked: 'blocked',
  expired: 'expired',
  revoked: 'revoked',
};

export function trangThaiUser(l: Pick<LicenseRpa, 'status' | 'is_lifetime' | 'expired_at'>, now = Date.now()): TrangThaiUserLicense {
  return THEO_USER[trangThaiHieuLuc(l, now)] ?? 'pending';
}

/** Bản ghi cũ (`user` hoặc trống) là Limited. */
export const loaiLicense = (l: Pick<LicenseRpa, 'license_type'>): LoaiLicense => (l.license_type === 'pro' ? 'pro' : 'limited');

/** Máy đang bind: thiết bị `active`; máy đã reset chỉ còn là lịch sử. */
export const thietBiDangBind = (l: Pick<LicenseRpa, 'devices'>): ThietBiLicense | undefined =>
  (l.devices ?? []).find((d) => d.status === 'active');

/** `FOXAI••••ABCD`: năm ký tự đầu, bốn ký tự cuối. */
export const anKhoa = (key: string) => (key ? `${key.slice(0, 5)}••••${key.slice(-4)}` : '');

/** Project OpenRPA của License; License cũ thì mã Project đã lưu. */
export const tenProjectLicense = (l: Pick<LicenseRpa, 'workflow_scope' | 'project_id'>) =>
  l.workflow_scope?.projectName || String(l.project_id ?? '');

/** Số ngày tới hạn (âm khi đã quá); `null` khi vĩnh viễn hoặc không đọc được ngày. */
export function soNgayConLai(l: Pick<LicenseRpa, 'is_lifetime' | 'expired_at'>, now = Date.now()): number | null {
  if (l.is_lifetime) return null;
  const h = hanMs(l.expired_at);
  return Number.isFinite(h) ? Math.floor((h - now) / MS_NGAY) : null;
}

/** Workflow License được dùng: phạm vi đã chọn; License cũ thì mọi Workflow của Project OpenRPA cùng mã. */
export function workflowCuaLicense(l: Pick<LicenseRpa, 'workflow_scope' | 'project_id'>, projects: readonly ProjectOpenRpa[]) {
  if (l.workflow_scope) return l.workflow_scope.workflows;
  return projects.find((p) => p.id === l.project_id)?.workflows.map((w) => ({ id: w.id, name: w.name })) ?? [];
}

// ─────────────── Bảng User License ───────────────

/** Một dòng = một License (gắn User nếu có), hoặc một User OpenCore chưa có License nào (`none`). */
export interface DongUserLicense {
  khoa: string;
  user: UserOpenCore | undefined;
  license: LicenseRpa | null;
  status: TrangThaiUserLicense | 'none';
}

export function dungDong(licenses: readonly LicenseRpa[], users: readonly UserOpenCore[], now = Date.now()): DongUserLicense[] {
  const theoId = new Map(users.map((u) => [u.id, u]));
  const coLicense = new Set<string>();
  const dong: DongUserLicense[] = licenses.map((l) => {
    if (l.opencore_user_id) coLicense.add(l.opencore_user_id);
    return { khoa: `l:${l.id}`, user: theoId.get(l.opencore_user_id ?? ''), license: l, status: trangThaiUser(l, now) };
  });
  for (const u of users) if (!coLicense.has(u.id)) dong.push({ khoa: `u:${u.id}`, user: u, license: null, status: 'none' });
  return dong;
}

export interface BoLocLicense {
  tim: string;
  status: string;
  thietBi: string;
}

export function locDong(dong: readonly DongUserLicense[], loc: BoLocLicense): DongUserLicense[] {
  const q = loc.tim.trim().toLowerCase();
  return dong.filter((r) => {
    // "Chưa được cấp License" gồm cả User chưa có bản ghi lẫn License đang chờ.
    if (loc.status === 'pending' ? r.status !== 'pending' && r.status !== 'none' : loc.status && r.status !== loc.status) return false;
    if (loc.thietBi) {
      const bind = r.license ? Boolean(thietBiDangBind(r.license)) : false;
      if ((loc.thietBi === 'bound') !== bind) return false;
    }
    if (!q) return true;
    const l = r.license;
    return [r.user?.username, r.user?.email, r.user?.name, l?.opencore_username, l?.opencore_email, l?.customer_name, l?.project_id,
      l && tenProjectLicense(l)]
      .map((x) => (x == null ? '' : String(x)))
      .join(' ')
      .toLowerCase()
      .includes(q);
  });
}

/** Active, có thời hạn và còn 0–60 ngày. */
export function demSapHetHan(dong: readonly DongUserLicense[], now = Date.now()): number {
  return dong.filter((r) => {
    if (r.status !== 'active' || !r.license) return false;
    const d = soNgayConLai(r.license, now);
    return d !== null && d >= 0 && d <= NGAY_SAP_HET_HAN;
  }).length;
}

export type TruyCapLicense = 'userDisabled' | 'syncError' | 'running' | 'syncing' | 'inactive';

/** Quyền chạy thực tế trên OpenCore: User bị khoá thắng mọi thứ, rồi đến lỗi đồng bộ. */
export function truyCap(r: DongUserLicense): TruyCapLicense | null {
  const l = r.license;
  if (!l) return null;
  if (r.user?.disabled) return 'userDisabled';
  if (l.access_sync_status === 'error') return 'syncError';
  if (r.status === 'active') return l.invoke_granted ? 'running' : 'syncing';
  return 'inactive';
}

/** License chưa thu hồi cấp trong một Project OpenRPA (License cũ theo mã Project không tính). */
export function licenseCuaProject(ds: readonly LicenseRpa[] | undefined, projectId: string, now = Date.now()): LicenseRpa[] {
  return (ds ?? []).filter((l) => l.workflow_scope?.projectId === projectId && trangThaiUser(l, now) !== 'revoked');
}

/** User đang có License active (không trùng) và số License active sắp hết hạn — hai ô KPI của màn Project. */
export function kpiLicenseProject(ds: readonly LicenseRpa[], now = Date.now()): { userCoLicense: number; sapHetHan: number } {
  const active = ds.filter((l) => trangThaiUser(l, now) === 'active');
  return {
    userCoLicense: new Set(active.map((l) => l.opencore_user_id).filter(Boolean)).size,
    sapHetHan: active.filter((l) => {
      const d = soNgayConLai(l, now);
      return d !== null && d >= 0 && d <= NGAY_SAP_HET_HAN;
    }).length,
  };
}

// ─────────────── Biểu mẫu cấp / sửa ───────────────

export interface FormLicense {
  userId: string;
  projectId: string;
  workflowIds: string[];
  tier: LoaiLicense;
  /** `YYYY-MM-DD` hoặc rỗng. */
  expiry: string;
  lifetime: boolean;
  status: TrangThaiUserLicense;
  note: string;
}

export function formTuLicense(l: LicenseRpa | null, userSan = '', now = Date.now()): FormLicense {
  return {
    userId: l?.opencore_user_id ?? userSan,
    projectId: l?.workflow_scope?.projectId ?? l?.project_id ?? '',
    workflowIds: l?.workflow_scope?.workflows.map((w) => w.id) ?? [],
    tier: l ? loaiLicense(l) : 'limited',
    expiry: String(l?.expired_at ?? '').slice(0, 10),
    lifetime: Boolean(l?.is_lifetime),
    status: l ? trangThaiUser(l, now) : 'active',
    note: l?.note ?? '',
  };
}

export type LoiFormLicense =
  | 'userRequired'
  | 'projectRequired'
  | 'workflowsRequired'
  | 'expiryRequired'
  | 'activeNeedsFuture'
  | 'duplicate';

/**
 * Kiểm theo thứ tự native, trả lỗi đầu tiên. `legacy`: License cũ không có phạm vi Workflow, sửa thì giữ nguyên phạm
 * vi. Trùng: một User một License chưa thu hồi trong mỗi Project (`uq_rpa_license_project_user`).
 */
export function kiemFormLicense(
  v: FormLicense,
  o: { goc: LicenseRpa | null; legacy: boolean; licenses: readonly LicenseRpa[]; now?: number },
): LoiFormLicense | null {
  const now = o.now ?? Date.now();
  if (!v.userId) return 'userRequired';
  if (!o.goc && !v.projectId) return 'projectRequired';
  if (!o.legacy && v.workflowIds.length === 0) return 'workflowsRequired';
  if (!v.lifetime && !v.expiry) return 'expiryRequired';
  if (v.status === 'active' && !v.lifetime && !(hanMs(v.expiry) > now)) return 'activeNeedsFuture';
  const trung = o.licenses.some(
    (l) => l.id !== o.goc?.id && l.opencore_user_id === v.userId && l.project_id === v.projectId && trangThaiUser(l, now) !== 'revoked',
  );
  return trung ? 'duplicate' : null;
}

/** `YYYY-MM-DD` theo giờ máy người dùng — ngày cấp. */
export function homNay(d = new Date()): string {
  const hai = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${hai(d.getMonth() + 1)}-${hai(d.getDate())}`;
}

export function thanCap(v: FormLicense, ngayCap: string): ThanCapLicense {
  return {
    customerName: '',
    projectId: v.projectId,
    expiredAt: v.lifetime ? null : v.expiry,
    isLifetime: v.lifetime,
    licenseType: v.lifetime ? 'lifetime' : 'standard',
    maxRobots: SO_MAY_MAC_DINH,
    note: v.note.trim(),
    opencore_user_id: v.userId,
    issuedAt: ngayCap,
    workflowIds: v.workflowIds,
    status: v.status === 'active' ? 'active' : 'pending',
    tier: v.tier,
  };
}

export const cungTap = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x));

/** Chỉ gửi trường đổi so với License đang có; ghi chú và thời hạn luôn gửi như native. */
export function thanSua(v: FormLicense, goc: LicenseRpa, legacy: boolean, now = Date.now()): ThanSuaLicense {
  const than: ThanSuaLicense = { note: v.note.trim(), isLifetime: v.lifetime, expiredAt: v.lifetime ? null : v.expiry };
  if (v.userId !== (goc.opencore_user_id ?? '')) than.opencore_user_id = v.userId || null;
  if (v.status !== trangThaiUser(goc, now)) than.status = v.status;
  if (v.tier !== loaiLicense(goc)) than.tier = v.tier;
  const cu = goc.workflow_scope?.workflows.map((w) => w.id) ?? [];
  if (!legacy && !cungTap(v.workflowIds, cu)) than.workflowIds = v.workflowIds;
  return than;
}

/** Gia hạn: có ngày mới ở tương lai, hoặc chọn vĩnh viễn. */
export function kiemGiaHan(expiry: string, lifetime: boolean, now = Date.now()): 'expiryRequired' | 'expiryPast' | null {
  if (lifetime) return null;
  if (!expiry) return 'expiryRequired';
  return hanMs(expiry) > now ? null : 'expiryPast';
}
